import Link from 'next/link'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/css'
import {
  ArrowRight,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Gauge,
  ImageOff,
  Info,
  Ruler,
  Settings,
  ShieldCheck,
  Weight,
  X,
  XCircle,
  Zap,
  ZoomIn,
} from 'lucide-react'
import toast from 'react-hot-toast'
import Seo from '../../components/Seo'
import { formatTND } from '../../lib/currency'
import { addToCart } from '../../lib/cart'
import { useI18n } from '../../context/I18nContext'
import { useAuth } from '../../context/AuthContext'
import ImageOnlySkeletonLoader from '../../components/ImageOnlySkeletonLoader'
import ShortcutsSection from '../../components/ShortcutsSection'
import { DEFAULT_SHORTCUTS } from '../../components/shortcutsData'
import { getBrandImageSrc } from '../../lib/brandImage'
import { getImageUrl, normalizeImageList } from '../../lib/imageData'
import { getActivePromo, getPromoFlashColor, getPromoToneClasses } from '../../lib/promo'
import { getProductBrand } from '../../lib/productBrand'
import { normalizeIdList } from '../../lib/productRelations'
import { absoluteUrl, getSiteUrl } from '../../lib/seo'
import ProductRelatedProductsSection from '../../components/product-details/ProductRelatedProductsSection'
import { getLanguageFromCookie } from '../../lib/language'
import { getRequestOrigin } from '../../lib/requestOrigin'

const PENDING_CART_KEY = 'etc_pending_cart_add'

function formatSpecLabel(key = '') {
  return String(key || '')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

function formatSpecValue(value, t) {
  if (typeof value === 'boolean') return value ? t('common.yes') : t('common.no')
  if (typeof value === 'number') return String(value)
  return String(value || '').trim()
}

function normalizeSpecKey(value = '') {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

function detectSpecGroup({ key, label }) {
  const raw = `${key || ''} ${label || ''}`.toLowerCase()
  const normalizedKey = normalizeSpecKey(key)
  const matchers = {
    weight: [
      'weight',
      'mass',
      'self_weight',
      'service_weight',
      'counterweight',
      'gross_weight',
    ],
    wheels_chassis: [
      'wheel',
      'wheels',
      'tire',
      'tyre',
      'wheelbase',
      'track',
      'overhang',
      'axle',
      'chassis',
    ],
    dimensions: [
      'dimension',
      'dimensions',
      'height',
      'width',
      'length',
      'radius',
      'clearance',
      'center',
      'mast',
      'fork',
    ],
    performance: [
      'speed',
      'capacity',
      'power',
      'output',
      'torque',
      'lift',
      'lifting',
      'load',
      'rated',
      'rpm',
      'battery',
      'engine',
    ],
  }

  for (const [group, tokens] of Object.entries(matchers)) {
    if (tokens.some((token) => raw.includes(token) || normalizedKey.includes(token))) {
      return group
    }
  }

  return 'general'
}

function getSpecDisplayValue({ specsMap, fieldMetaByKey, key, t }) {
  if (!specsMap || !specsMap.has(key)) return null
  const value = specsMap.get(key)
  if (value === null || value === undefined || value === '') return null
  const unit = fieldMetaByKey.get(key)?.unit ? ` ${fieldMetaByKey.get(key).unit}` : ''
  return `${formatSpecValue(value, t)}${unit}`
}

function titleCase(value = '') {
  return String(value || '')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

function resolveLocalizedLabel({ language, key = '', labelI18n = {}, fallbackLabel = '' }) {
  const normalizedKey = String(key || '').trim()
  const keyLabel = titleCase(normalizedKey)
  const baseLabel = String(fallbackLabel || '').trim()
  const en = String(labelI18n?.en || '').trim()
  const fr = String(labelI18n?.fr || '').trim()

  if (language === 'en') {
    if (en && fr && en.toLowerCase() !== fr.toLowerCase()) return en
    if (en && !fr) return en
    return keyLabel || baseLabel || en || fr || normalizedKey
  }
  if (language === 'fr') {
    return fr || baseLabel || keyLabel || en || normalizedKey
  }
  return baseLabel || keyLabel || en || fr || normalizedKey
}

function getLocalizedShowroomName(showroom = {}, language = 'en') {
  const normalizedLanguage = String(language || 'en').trim().toLowerCase() === 'fr' ? 'fr' : 'en';
  const fallbackLanguage = normalizedLanguage === 'fr' ? 'en' : 'fr';
  return (
    showroom?.translations?.[normalizedLanguage]?.name ||
    showroom?.translations?.[fallbackLanguage]?.name ||
    showroom?.name ||
    ''
  );
}

const PRODUCT_TYPE_KEY_ALIASES = {
  chariot_elevateur: 'forklift',
  chariots_elevateurs: 'forklift',
  transpalette: 'pallet_truck',
  transpalettes: 'pallet_truck',
  gerbeur: 'stacker',
  gerbeurs: 'stacker',
  tracteur: 'tractor',
  tracteurs: 'tractor',
  non_classe: 'unclassified',
  non_classes: 'unclassified',
}

function normalizeTypeKey(value = '') {
  return String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

function resolveProductTypeKey(value = '') {
  const normalized = normalizeTypeKey(value)
  if (!normalized) return ''
  return PRODUCT_TYPE_KEY_ALIASES[normalized] || normalized
}

function normalizeSpecsObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {}
}

async function fetchJsonOrThrow(url, options = {}) {
  const response = await fetch(url, { credentials: 'include', ...options })
  const raw = await response.text()
  let data = {}
  try {
    data = raw ? JSON.parse(raw) : {}
  } catch (error) {
    data = { error: raw || 'Invalid JSON response' }
  }
  if (!response.ok) {
    const error = new Error(data?.error || 'Request failed')
    error.status = response.status
    throw error
  }
  return data
}

function buildProductTypeMetaMap(types = [], language = 'en') {
  const resolveTypeLabel = (item) =>
    resolveLocalizedLabel({
      language,
      key: item?.key,
      labelI18n: item?.labelI18n,
      fallbackLabel: item?.label,
    })
  const resolveFieldLabel = (field) =>
    resolveLocalizedLabel({
      language,
      key: field?.key,
      labelI18n: field?.labelI18n,
      fallbackLabel: field?.label,
    })

  return new Map(
    (Array.isArray(types) ? types : []).map((item) => [
      item.key,
      {
        label: resolveTypeLabel(item),
        labelI18n: item?.labelI18n || {},
        fields: new Map(
          Array.isArray(item.fields)
            ? item.fields.map((field) => [
                field.key,
                {
                  label: resolveFieldLabel(field),
                  unit: field.unit || '',
                },
              ])
            : [],
        ),
      },
    ]),
  )
}

async function fetchProductWithFallback(id, language) {
  const normalizedId = String(id || '').trim()
  if (!normalizedId) {
    throw new Error('Missing product id')
  }

  try {
    return await fetchJsonOrThrow(`/api/products/${normalizedId}?lang=${language}`, { cache: 'no-store' })
  } catch (detailError) {
    const products = await fetchJsonOrThrow(`/api/products?lang=${language}`, { cache: 'no-store' })
    const productList = Array.isArray(products) ? products : []
    const fallbackProduct = productList.find((item) => {
      const itemId = String(item?._id || '').trim()
      const itemSlug = String(item?.slug || '').trim()
      return itemId === normalizedId || itemSlug === normalizedId
    })

    if (fallbackProduct) {
      return fallbackProduct
    }

    throw detailError
  }
}

export default function ProductPage({
  initialId = '',
  initialProduct = null,
  initialProductTypes = [],
  initialProductsCatalog = [],
}) {
  const router = useRouter()
  const { t, language } = useI18n()
  const { isAuthenticated } = useAuth()
  const id = router.query?.id || initialId
  const [product, setProduct] = useState(initialProduct || null)
  const [productLoadError, setProductLoadError] = useState('')
  const [productTypeMetaByKey, setProductTypeMetaByKey] = useState(() =>
    buildProductTypeMetaMap(initialProductTypes, language),
  )
  const [productsCatalog, setProductsCatalog] = useState(initialProductsCatalog || [])
  const [sparePartsCatalog, setSparePartsCatalog] = useState([])
  const [activeSpecGroup, setActiveSpecGroup] = useState('all')
  const [showAllSpecs, setShowAllSpecs] = useState(false)
  const [activeImageIndex, setActiveImageIndex] = useState(0)
  const [isLightboxOpen, setIsLightboxOpen] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)
  const [isLightboxZoomed, setIsLightboxZoomed] = useState(false)
  const [isLightboxImageLoaded, setIsLightboxImageLoaded] = useState(false)
  const [lightboxMetaBySrc, setLightboxMetaBySrc] = useState({})
  const [isMounted, setIsMounted] = useState(false)
  const imageSwiperRef = useRef(null)
  const lightboxBodyStyleRef = useRef({ overflow: '', paddingRight: '' })

  useEffect(() => {
    if (!id) return
    let isCurrent = true

    Promise.all([
      fetchProductWithFallback(id, language),
      fetchJsonOrThrow('/api/product-types').catch(() => []),
      fetchJsonOrThrow(`/api/spare-parts?lang=${language}&paginated=false`).catch(() => []),
      fetchJsonOrThrow(`/api/products?lang=${language}&paginated=false`).catch(() => []),
    ])
      .then(([productData, typeData, sparePartsData, productsData]) => {
        if (!isCurrent) return
        if (!productData || typeof productData !== 'object' || productData.error) {
          throw new Error(t('productDetails.loadFailed', 'Failed to load product'))
        }
        setProductLoadError('')
        setProduct(productData)
        setProductsCatalog(Array.isArray(productsData) ? productsData : [])
        setSparePartsCatalog(Array.isArray(sparePartsData) ? sparePartsData : [])
        setProductTypeMetaByKey(buildProductTypeMetaMap(typeData, language))
      })
      .catch((error) => {
        if (!isCurrent) return
        setProduct(null)
        setProductsCatalog([])
        setProductLoadError(error?.message || t('productDetails.loadFailed', 'Failed to load product'))
      })

    return () => {
      isCurrent = false
    }
  }, [id, language, t])

  useEffect(() => {
    setShowAllSpecs(false)
  }, [id])

  const rawTypeKey = String(product?.productType || 'unclassified')
  const normalizedTypeKey = normalizeTypeKey(rawTypeKey)
  const resolvedTypeKey = resolveProductTypeKey(rawTypeKey) || normalizedTypeKey || 'unclassified'
  const typeMeta =
    (language === 'en' ? productTypeMetaByKey.get(resolvedTypeKey) : null) ||
    productTypeMetaByKey.get(rawTypeKey) ||
    productTypeMetaByKey.get(resolvedTypeKey) ||
    null
  const fieldMetaByKey = typeMeta?.fields || new Map()
  const rawSpecs = normalizeSpecsObject(product?.specsBase || product?.specs)
  const localizedSpecs = normalizeSpecsObject(product?.specsI18n?.[language])
  const frenchSpecs = normalizeSpecsObject(product?.specsI18n?.fr)
  const specKeys = new Set([...Object.keys(rawSpecs), ...Object.keys(localizedSpecs)])
  const specsEntries = Array.from(specKeys)
    .map((key) => {
      const localizedValue = localizedSpecs[key]
      if (localizedValue !== null && localizedValue !== undefined && localizedValue !== '') {
        return [key, localizedValue]
      }
      const fallbackValue = rawSpecs[key]
      if (fallbackValue === null || fallbackValue === undefined || fallbackValue === '') return null
      if (language === 'en' && typeof fallbackValue === 'string') {
        const frValue = frenchSpecs[key]
        if (frValue && String(frValue).trim() === String(fallbackValue).trim()) {
          return null
        }
      }
      return [key, fallbackValue]
    })
    .filter(Boolean)
  const specsMap = new Map(specsEntries)

  const specsWithGroups = specsEntries.map(([key, value]) => {
    const fieldLabel = fieldMetaByKey.get(key)?.label || ''
    return {
      key,
      value,
      group: detectSpecGroup({ key, label: fieldLabel }),
    }
  })

  const filteredSpecs =
    activeSpecGroup === 'all'
      ? specsWithGroups
      : specsWithGroups.filter((item) => item.group === activeSpecGroup)
  const visibleSpecs = filteredSpecs.slice(0, 6)
  const hiddenSpecs = filteredSpecs.slice(6)

  const capacityValue =
    getSpecDisplayValue({ specsMap, fieldMetaByKey, key: 'loading_capacity', t }) ||
    getSpecDisplayValue({ specsMap, fieldMetaByKey, key: 'capacity', t })
  const powerValue =
    getSpecDisplayValue({ specsMap, fieldMetaByKey, key: 'power_type', t }) ||
    getSpecDisplayValue({ specsMap, fieldMetaByKey, key: 'battery', t }) ||
    getSpecDisplayValue({ specsMap, fieldMetaByKey, key: 'engine_model', t })
  const highlightSpecKey = 'driving_speed'
  const highlightSpecValue = getSpecDisplayValue({ specsMap, fieldMetaByKey, key: highlightSpecKey, t })
  const highlightSpecLabel =
    fieldMetaByKey.get(highlightSpecKey)?.label || formatSpecLabel(highlightSpecKey)
  const hasCapacity = Boolean(capacityValue)
  const hasPower = Boolean(powerValue)
  const hasHighlightSpec = Boolean(highlightSpecValue)
  const normalizeDetailKey = (value) => String(value || '').toLowerCase().trim()
  const excludedDetailKeys = new Set([
    highlightSpecKey,
    'loading_capacity',
    'capacity',
    'power_type',
    'battery',
    'engine_model',
    'model',
    'model_name',
    'model_number',
  ].map(normalizeDetailKey))
  const preferredDetailKeys = [
    'load_center',
    'load_center_mm',
    'load_center_distance',
    'load_center_distance_mm',
    'load_centre',
    'center_of_load',
  ]
  const detailKeys = specsEntries.map(([key]) => key)
  const preferredResolvedKeys = preferredDetailKeys
    .map((pref) => detailKeys.find((key) => normalizeDetailKey(key) === pref))
    .filter(Boolean)
  const orderedDetailKeys = [
    ...preferredResolvedKeys,
    ...detailKeys.filter((key) => {
      const normalized = normalizeDetailKey(key)
      if (excludedDetailKeys.has(normalized)) return false
      if (preferredResolvedKeys.includes(key)) return false
      return true
    }),
  ]
  const extraDetailItems = orderedDetailKeys
    .filter((key) => !excludedDetailKeys.has(normalizeDetailKey(key)))
    .map((key) => {
      const value = getSpecDisplayValue({ specsMap, fieldMetaByKey, key, t })
      if (!value) return null
      const label = fieldMetaByKey.get(key)?.label || formatSpecLabel(key)
      return {
        key,
        label,
        value,
        group: detectSpecGroup({ key, label }),
      }
    })
    .filter(Boolean)
    .slice(0, Math.max(0, 4 - Number(hasHighlightSpec) - Number(hasCapacity) - Number(hasPower)))
  const detailIconByGroup = {
    performance: Gauge,
    dimensions: Ruler,
    wheels_chassis: Settings,
    weight: Weight,
    general: Info,
  }
  const shouldSpanPrimaryCard = !hasCapacity && !hasPower && extraDetailItems.length === 0

  const englishName = String(product?.translations?.en?.name || '').trim()
  const frenchName = String(product?.translations?.fr?.name || '').trim()
  const baseName = String(product?.name || '').trim()
  const slugName = titleCase(String(product?.slug || '').replace(/-/g, ' '))
  const displayName =
    language === 'en'
      ? englishName || (baseName && baseName !== frenchName ? baseName : '') || slugName || baseName
      : frenchName || baseName
  const englishDescription = String(product?.translations?.en?.description || '').trim()
  const frenchDescription = String(product?.translations?.fr?.description || '').trim()
  const baseDescription = String(product?.description || '').trim()
  const displayDescription =
    language === 'en'
      ? englishDescription || (baseDescription && baseDescription !== frenchDescription ? baseDescription : '')
      : frenchDescription || baseDescription
  const typeLabelEn = String(typeMeta?.labelI18n?.en || '').trim()
  const typeLabelFr = String(typeMeta?.labelI18n?.fr || '').trim()
  const fallbackTypeLabel =
    language === 'fr'
      ? titleCase(rawTypeKey || resolvedTypeKey || 'unclassified')
      : titleCase(resolvedTypeKey || rawTypeKey || 'unclassified')
  const displayTypeLabel =
    language === 'en' ? typeLabelEn || fallbackTypeLabel : typeLabelFr || typeMeta?.label || fallbackTypeLabel
  const brand = getProductBrand(product)
  const brandImageSrc = getBrandImageSrc(brand)
  const activePromo = getActivePromo(product, language)
  const promoTone = getPromoToneClasses(activePromo?.colorTone)
  const productImages = normalizeImageList(product?.images)
  const siteUrl = getSiteUrl()
  const productPageUrl = absoluteUrl(router.asPath || `/machines/${id}`, siteUrl)
  const productImageUrls = productImages.map((image) => absoluteUrl(getImageUrl(image), siteUrl)).filter(Boolean)
  const seoTitle =
    displayName && displayTypeLabel
      ? `${displayName} - ${displayTypeLabel}`
      : displayName || displayTypeLabel || t('productDetails.seoTitle', 'Machine details')
  const seoDescription =
    displayDescription ||
    t(
      'productDetails.seoDescription',
      'Explore technical specifications, availability, and related machines.',
    )
  const productJsonLd = product
    ? {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: displayName || product.name,
        description: seoDescription,
        sku: String(product?.slug || product?._id || '').trim(),
        image: productImageUrls,
        category: displayTypeLabel || undefined,
        brand: brand ? { '@type': 'Brand', name: brand } : undefined,
        offers: {
          '@type': 'Offer',
          url: productPageUrl,
          availability: Number(product?.stock ?? 0) > 0
            ? 'https://schema.org/InStock'
            : 'https://schema.org/OutOfStock',
          itemCondition: 'https://schema.org/NewCondition',
          seller: {
            '@type': 'Organization',
            name: 'ETC Company',
          },
        },
      }
    : null
  function handleAddToCart() {
    if (!product) return
    if (!isAuthenticated) {
      if (typeof window !== 'undefined') {
        sessionStorage.setItem(PENDING_CART_KEY, JSON.stringify({ ...product, quantity: 1, kind: 'product' }))
      }
      router.push(`/auth/login?next=${encodeURIComponent(router.asPath || `/machines/${id}`)}`)
      return
    }
    addToCart(product, 1, { kind: 'product' })
    toast.success(`${displayName || product.name} ${t('common.addedToCart', 'added to cart.')}`)
  }
  const compatibleSparePartIds = useMemo(
    () => normalizeIdList(product?.compatibleSparePartIds),
    [product?.compatibleSparePartIds],
  )
  const relatedProducts = useMemo(() => {
    const currentProductId = String(product?._id || product?.id || '').trim()
    const currentProductType = String(product?.productType || '').trim()
    const currentBrand = String(brand || '').trim()

    if (!currentProductId || !Array.isArray(productsCatalog) || productsCatalog.length === 0) {
      return []
    }

    const currentCompatibleSparePartIds = new Set(compatibleSparePartIds)

    const candidates = productsCatalog
      .filter((item) => {
        const itemId = String(item?._id || item?.id || '').trim()
        return itemId && itemId !== currentProductId
      })
      .map((item) => {
        const itemCompatibleSparePartIds = normalizeIdList(item?.compatibleSparePartIds)
        const sharedCompatibleSparePartCount = itemCompatibleSparePartIds.reduce(
          (count, sparePartId) => count + (currentCompatibleSparePartIds.has(sparePartId) ? 1 : 0),
          0,
        )

        return {
          item,
          sameBrand: currentBrand && getProductBrand(item) === currentBrand ? 1 : 0,
          sameType: String(item?.productType || '').trim() === currentProductType ? 1 : 0,
          sharedCompatibleSparePartCount,
          inStock: Number(item?.stock ?? 0) > 0 ? 1 : 0,
        }
      })

    return candidates
      .sort((a, b) => {
        return (
          b.sameBrand - a.sameBrand ||
          b.sameType - a.sameType ||
          b.sharedCompatibleSparePartCount - a.sharedCompatibleSparePartCount ||
          b.inStock - a.inStock
        )
      })
      .map(({ item }) => item)
      .slice(0, 3)
  }, [
    brand,
    compatibleSparePartIds,
    product?._id,
    product?.id,
    product?.productType,
    productsCatalog,
  ])
  const productShowrooms = Array.isArray(product?.showrooms) ? product.showrooms : []
  const hasMultipleImages = productImages.length > 1
  const hasImages = productImages.length > 0
  const activeLightboxImage = hasImages ? productImages[lightboxIndex] : null
  const activeLightboxSrc = getImageUrl(activeLightboxImage)

  const openLightbox = (index = 0) => {
    if (!hasImages) return
    setIsLightboxImageLoaded(false)
    setLightboxIndex(index)
    setIsLightboxOpen(true)
  }

  const closeLightbox = useCallback(() => {
    setIsLightboxOpen(false)
    if (imageSwiperRef.current && hasMultipleImages) {
      if (typeof imageSwiperRef.current.slideToLoop === 'function') {
        imageSwiperRef.current.slideToLoop(lightboxIndex)
      } else {
        imageSwiperRef.current.slideTo(lightboxIndex)
      }
    }
    setActiveImageIndex(lightboxIndex)
  }, [hasMultipleImages, lightboxIndex])

  const goLightboxPrev = useCallback(() => {
    if (!hasImages) return
    setIsLightboxImageLoaded(false)
    setLightboxIndex((prev) => (prev - 1 + productImages.length) % productImages.length)
  }, [hasImages, productImages])

  const goLightboxNext = useCallback(() => {
    if (!hasImages) return
    setIsLightboxImageLoaded(false)
    setLightboxIndex((prev) => (prev + 1) % productImages.length)
  }, [hasImages, productImages])

  useEffect(() => {
    if (!hasImages) return
    setLightboxIndex((prev) => Math.min(prev, productImages.length - 1))
  }, [hasImages, productImages.length])

  useEffect(() => {
    setIsMounted(true)
  }, [])

  useEffect(() => {
    if (!isLightboxOpen) {
      setIsLightboxZoomed(false)
      return
    }
    setIsLightboxZoomed(false)
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeLightbox()
        return
      }
      if (!hasMultipleImages) return
      if (event.key === 'ArrowLeft') {
        goLightboxPrev()
      }
      if (event.key === 'ArrowRight') {
        goLightboxNext()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [closeLightbox, goLightboxNext, goLightboxPrev, hasMultipleImages, isLightboxOpen])

  useEffect(() => {
    if (!isLightboxOpen || typeof document === 'undefined') return
    const { body, documentElement } = document
    lightboxBodyStyleRef.current = {
      overflow: body.style.overflow,
      paddingRight: body.style.paddingRight,
    }
    const scrollbarWidth = window.innerWidth - documentElement.clientWidth
    body.style.overflow = 'hidden'
    if (scrollbarWidth > 0) {
      body.style.paddingRight = `${scrollbarWidth}px`
    }
    return () => {
      body.style.overflow = lightboxBodyStyleRef.current.overflow
      body.style.paddingRight = lightboxBodyStyleRef.current.paddingRight
    }
  }, [isLightboxOpen])

  useEffect(() => {
    setActiveImageIndex(0)
    if (imageSwiperRef.current) {
      imageSwiperRef.current.slideTo(0, 0)
    }
  }, [id, productImages.length])

  useEffect(() => {
    if (!router.isReady || !product) return
    const nextTypeLabel = displayTypeLabel || product.productType || ''
    const nextProductName = displayName || product.name || ''
    const nextTypeKey = product.productType || ''
    const normalize = (value) => (Array.isArray(value) ? value[0] : value)
    const currentName = String(normalize(router.query?.productName) || '')
    const currentTypeLabel = String(normalize(router.query?.productTypeLabel) || '')
    const currentTypeKey = String(normalize(router.query?.productType) || '')
    if (
      currentName === String(nextProductName) &&
      currentTypeLabel === String(nextTypeLabel) &&
      currentTypeKey === String(nextTypeKey)
    ) {
      return
    }

    const nextQuery = {
      ...router.query,
      productName: nextProductName,
      productTypeLabel: nextTypeLabel,
      productType: nextTypeKey,
    }
    const dynamicKeys = Array.from(router.pathname.matchAll(/\[([^\]]+)\]/g)).map((match) => match[1])
    const searchParams = new URLSearchParams()
    Object.entries(nextQuery).forEach(([key, value]) => {
      if (dynamicKeys.includes(key)) return
      if (value === null || value === undefined || value === '') return
      searchParams.set(key, String(value))
    })
    const basePath = router.asPath.split('?')[0]
    const nextAsPath = searchParams.toString() ? `${basePath}?${searchParams}` : basePath
    if (nextAsPath === router.asPath) return

    router.replace({ pathname: router.pathname, query: nextQuery }, nextAsPath, { shallow: true, scroll: false })
  }, [product, typeMeta, displayName, displayTypeLabel, router])

  const isProductLoading = !product && !productLoadError

  if (isProductLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--dash-blacker)] px-6 text-white">
        <div className="flex flex-col items-center gap-4 text-center">
          <div className="h-12 w-12 animate-spin rounded-full border-2 border-[var(--dash-border)] border-t-[var(--dash-primary)]" />
          <p className="text-xs font-bold uppercase tracking-[0.24em] text-white/50">
            {t('productDetails.loading', 'Loading product...')}
          </p>
        </div>
      </div>
    )
  }

  if (!product) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center bg-[var(--dash-blacker)] text-white">
        {productLoadError || t('productDetails.loading')}
      </div>
    )
  }

  const lightboxOverlay = isLightboxOpen && hasImages ? (
    <div
      className="fixed inset-0 z-[9999] bg-black/90 backdrop-blur-sm"
      onClick={(event) => {
        if (event.target !== event.currentTarget) return
        closeLightbox()
      }}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="relative mx-auto flex h-full w-full max-w-6xl flex-col gap-4 p-6"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="rounded-full border border-white/15 bg-black/70 px-3 py-1 text-[11px] font-semibold text-white/80">
            {displayName}
          </div>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-white/60">
            {lightboxIndex + 1} / {productImages.length}
          </div>
          <button
            type="button"
            onClick={closeLightbox}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-black/70 text-white/80 transition hover:text-white hover:border-white/30"
            aria-label={t('productDetails.closeGallery', 'Close gallery')}
          >
            <X size={18} />
          </button>
        </div>
        <div className="relative flex flex-1 items-center justify-center overflow-hidden rounded-2xl border border-white/10 bg-black/60">
          <ImageOnlySkeletonLoader
            image={activeLightboxSrc}
            alt={`${displayName} ${lightboxIndex + 1}`}
            onClick={() => setIsLightboxZoomed((prev) => !prev)}
            onLoad={(event) => {
              const { naturalWidth, naturalHeight } = event.currentTarget
              const src = event.currentTarget.currentSrc || activeLightboxSrc
              if (!src || !naturalWidth || !naturalHeight) return
              requestAnimationFrame(() => setIsLightboxImageLoaded(true))
              setLightboxMetaBySrc((prev) => {
                if (prev[src]?.width === naturalWidth && prev[src]?.height === naturalHeight) return prev
                return { ...prev, [src]: { width: naturalWidth, height: naturalHeight } }
              })
            }}
            containerClassName="h-full w-full"
            imgClassName={`h-full w-full object-contain transition-all duration-300 ${
              isLightboxZoomed ? 'scale-125 cursor-zoom-out' : 'scale-100 cursor-zoom-in'
            } ${isLightboxImageLoaded ? 'opacity-100' : 'opacity-0'}`}
            watermark
          />
        </div>

        {hasMultipleImages ? (
          <div className="flex flex-wrap items-center justify-center gap-3 pb-1">
            {productImages.map((image, index) => (
              <button
                key={`${getImageUrl(image)}-lightbox-${index}`}
                type="button"
                onClick={() => {
                  setIsLightboxImageLoaded(false)
                  setLightboxIndex(index)
                }}
                className={`relative h-16 w-20 flex-shrink-0 overflow-hidden rounded-xl border transition-all ${
                  lightboxIndex === index
                    ? 'border-[var(--dash-primary)]'
                    : 'border-white/10 opacity-70 hover:opacity-100'
                }`}
                aria-pressed={lightboxIndex === index}
              >
                <ImageOnlySkeletonLoader
                  image={image}
                  alt={`${displayName} thumbnail ${index + 1}`}
                  containerClassName="h-full w-full"
                  imgClassName="h-full w-full object-cover"
                  watermark
                />
              </button>
            ))}
          </div>
        ) : null}
      </div>
      {hasMultipleImages ? (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-between px-4 sm:px-8">
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation()
              goLightboxPrev()
            }}
            className="pointer-events-auto flex h-12 w-12 items-center justify-center rounded-full border border-white/15 bg-black/70 text-white/80 transition hover:text-white hover:border-white/30"
            aria-label={t('productDetails.previousImage', 'Previous image')}
          >
            <ChevronLeft size={22} />
          </button>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation()
              goLightboxNext()
            }}
            className="pointer-events-auto flex h-12 w-12 items-center justify-center rounded-full border border-white/15 bg-black/70 text-white/80 transition hover:text-white hover:border-white/30"
            aria-label={t('productDetails.nextImage', 'Next image')}
          >
            <ChevronRight size={22} />
          </button>
        </div>
      ) : null}
    </div>
  ) : null

  return (
    <div className="min-h-screen bg-[var(--dash-blacker)] text-white">
      <Seo
        title={seoTitle}
        description={seoDescription}
        keywords={{
          en: [
            displayName,
            displayTypeLabel,
            brand,
            'forklift',
            'warehouse equipment',
            'industrial machine',
            'material handling equipment',
          ],
          fr: [
            displayName,
            displayTypeLabel,
            brand,
            'chariot elevateur',
            'materiel de manutention',
            'machine industrielle',
            'equipement de manutention',
          ],
        }}
        jsonLd={productJsonLd ? [productJsonLd] : []}
      />
      <section
        className="relative bg-[var(--dash-blacker)]"
      >
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute inset-0 opacity-20 bg-[url('/bg/5.webp')] bg-no-repeat bg-[size:100%_100%]" />
          <div className="absolute left-0 top-0 h-full w-full bg-[radial-gradient(circle_at_15%_25%,rgba(255,93,16,0.08)_0%,transparent_40%)]" />
          <div className="absolute right-0 top-0 h-[600px] w-[600px] translate-x-1/4 -translate-y-1/2 rounded-full bg-[#ff5d10]/5 blur-[120px]" />
          <div className="absolute bottom-0 left-0 h-[500px] w-[500px] -translate-x-1/4 translate-y-1/2 rounded-full bg-blue-500/5 blur-[100px]" />
          <div
            aria-hidden="true"
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: 'radial-gradient(#ffffff 0.5px, transparent 0.5px)',
              backgroundSize: '40px 40px',
            }}
          />
        </div>

        <div className="container relative z-10 mx-auto px-6 lg:px-10">
          <div className="relative z-10 w-full pt-0 pb-28 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12 items-start">
          <div className="lg:col-span-7 lg:mb-0 lg:sticky lg:top-24 lg:self-start space-y-4">
            {productImages.length > 0 ? (
              <div className="flex flex-col gap-4 md:h-[520px] md:flex-row md:items-stretch lg:h-[600px]">
                {hasMultipleImages ? (
                  <div className="order-2 flex flex-wrap gap-3 md:order-1 md:w-20 md:shrink-0 md:flex-col md:flex-nowrap md:gap-4">
                    {productImages.map((image, index) => (
                      <button
                        key={`${getImageUrl(image)}-thumb-${index}`}
                        type="button"
                        onClick={() => {
                          const swiper = imageSwiperRef.current
                          if (!swiper) return
                          if (hasMultipleImages && typeof swiper.slideToLoop === 'function') {
                            swiper.slideToLoop(index)
                          } else {
                            swiper.slideTo(index)
                          }
                        }}
                        className={`group relative aspect-square w-20 flex-none overflow-hidden rounded-xl border transition-all duration-300 md:h-20 md:w-full ${
                          activeImageIndex === index
                            ? 'border-[var(--dash-primary)]'
                            : 'border-white/5 opacity-50 hover:opacity-100'
                        }`}
                        aria-pressed={activeImageIndex === index}
                      >
                        <ImageOnlySkeletonLoader
                          image={image}
                          alt={`${displayName} thumbnail ${index + 1}`}
                          loading="lazy"
                          containerClassName="h-full w-full"
                          imgClassName="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                          watermark
                        />
                      </button>
                    ))}
                  </div>
                ) : null}
                <div className="order-1 relative flex w-full flex-1 items-center justify-center overflow-hidden rounded-3xl border border-white/10 bg-white/5 aspect-[4/3] md:order-2 md:h-full md:min-h-0 md:aspect-auto">
                  <Swiper
                    slidesPerView={1}
                    spaceBetween={0}
                    loop={hasMultipleImages}
                    grabCursor={hasMultipleImages}
                    allowTouchMove={hasMultipleImages}
                    onSwiper={(swiper) => {
                      imageSwiperRef.current = swiper
                      setActiveImageIndex(Number.isFinite(swiper.realIndex) ? swiper.realIndex : 0)
                    }}
                    onSlideChange={(swiper) => {
                      const idx = Number.isFinite(swiper.realIndex) ? swiper.realIndex : swiper.activeIndex
                      setActiveImageIndex(((idx % productImages.length) + productImages.length) % productImages.length)
                    }}
                    className="h-full w-full"
                  >
                    {productImages.map((image, index) => (
                      <SwiperSlide key={`${getImageUrl(image)}-${index}`} className="h-full">
                        <div className="flex h-full w-full items-center justify-center">
                          <ImageOnlySkeletonLoader
                            image={image}
                            alt={`${displayName} ${index + 1}`}
                            loading={index === 0 ? 'eager' : 'lazy'}
                            fetchPriority={index === 0 ? 'high' : 'auto'}
                            onClick={() => openLightbox(index)}
                            containerClassName="h-full w-full"
                            imgClassName="h-full w-full object-cover cursor-zoom-in"
                            watermark
                          />
                        </div>
                      </SwiperSlide>
                    ))}
                  </Swiper>
                  {typeMeta?.label ? (
                    <div className="absolute left-6 top-6 bg-black/60 backdrop-blur px-4 py-2 rounded-full text-[10px] font-bold uppercase tracking-widest text-white/80 border border-white/10">
                      {displayTypeLabel}
                    </div>
                  ) : null}
                  {hasImages ? (
                    <div className="absolute bottom-6 right-6 z-20 rounded-full border border-white/10 bg-black/60 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.28em] text-white/80 backdrop-blur">
                      {String(activeImageIndex + 1).padStart(2, '0')} / {String(productImages.length).padStart(2, '0')}
                    </div>
                  ) : null}
                  {hasImages ? (
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation()
                        openLightbox(activeImageIndex)
                      }}
                      className="absolute right-6 top-6 z-20 group flex h-11 w-11 items-center justify-center overflow-hidden rounded-full"
                      aria-label={t('productDetails.openImageGallery', 'Open image gallery')}
                    >
                      <span className="absolute inset-0 scale-100 rounded-full bg-[var(--dash-main)] transition-all duration-700 group-hover:scale-150 group-hover:opacity-0" />
                      <span className="absolute inset-0 scale-0 rounded-full bg-[var(--dash-other)] transition-transform duration-500 group-hover:scale-100" />
                      <ZoomIn size={18} className="relative z-10 text-white transition-colors group-hover:text-[var(--dash-black)]" />
                    </button>
                  ) : null}
                  {hasImages ? (
                    <div className="absolute bottom-6 left-6 z-20 flex items-center gap-2">
                      <button
                        type="button"
                        disabled={!hasMultipleImages}
                        onClick={() => imageSwiperRef.current?.slidePrev()}
                        className={`group relative flex h-11 w-11 items-center justify-center overflow-hidden rounded-full transition ${
                          hasMultipleImages ? '' : 'opacity-40 cursor-not-allowed'
                        }`}
                        aria-label={t('productDetails.previousImage', 'Previous image')}
                      >
                        <span className="absolute inset-0 scale-100 rounded-full bg-[var(--dash-main)] transition-all duration-700 group-hover:scale-150 group-hover:opacity-0" />
                        <span className="absolute inset-0 scale-0 rounded-full bg-[var(--dash-other)] transition-transform duration-500 group-hover:scale-100" />
                        <ChevronLeft
                          size={18}
                          className="relative z-10 text-white transition-colors group-hover:text-[var(--dash-black)]"
                        />
                      </button>
                      <button
                        type="button"
                        disabled={!hasMultipleImages}
                        onClick={() => imageSwiperRef.current?.slideNext()}
                        className={`group relative flex h-11 w-11 items-center justify-center overflow-hidden rounded-full transition ${
                          hasMultipleImages ? '' : 'opacity-40 cursor-not-allowed'
                        }`}
                        aria-label={t('productDetails.nextImage', 'Next image')}
                      >
                        <span className="absolute inset-0 scale-100 rounded-full bg-[var(--dash-main)] transition-all duration-700 group-hover:scale-150 group-hover:opacity-0" />
                        <span className="absolute inset-0 scale-0 rounded-full bg-[var(--dash-other)] transition-transform duration-500 group-hover:scale-100" />
                        <ChevronRight
                          size={18}
                          className="relative z-10 text-white transition-colors group-hover:text-[var(--dash-black)]"
                        />
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : (
              <div className="relative aspect-[4/3] overflow-hidden rounded-3xl border border-white/10 bg-[var(--dash-blacker)] md:h-[520px] md:min-h-0 md:aspect-auto lg:h-[600px]">
                {typeMeta?.label ? (
                  <div className="absolute left-6 top-6 bg-black/60 backdrop-blur px-4 py-2 rounded-full text-[10px] font-bold uppercase tracking-widest text-white/80 border border-white/10">
                    {displayTypeLabel}
                  </div>
                ) : null}
                <div className="absolute inset-x-6 bottom-6 flex gap-3 opacity-70">
                  <div className="h-16 flex-1 rounded-2xl border border-white/10 bg-white/[0.04]" />
                  <div className="h-16 w-20 rounded-2xl border border-white/10 bg-white/[0.03]" />
                  <div className="h-16 w-20 rounded-2xl border border-white/10 bg-white/[0.03]" />
                </div>
                <div className="relative flex h-full flex-col items-center justify-center px-6 text-center">
                  <div className="flex h-20 w-20 items-center justify-center rounded-[28px] border border-white/10 bg-white/5 text-[var(--dash-primary)] shadow-[0_20px_50px_-30px_rgba(var(--dash-main-rgb),0.5)]">
                    <ImageOff className="h-9 w-9" />
                  </div>
                  <p className="mt-5 text-sm font-bold uppercase tracking-[0.32em] text-white/45">
                    {t('common.noImage')}
                  </p>
                  <p className="mt-3 max-w-sm text-sm leading-relaxed text-white/60">
                    {displayName}
                  </p>
                  <p className="mt-2 max-w-xs text-xs uppercase tracking-[0.18em] text-white/35">
                    {t('productDetails.visualComingSoon')}
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="lg:col-span-5 space-y-8">
            <div className="relative space-y-4">
              <div className="pr-20 md:pr-24">
                <div className="min-w-0 space-y-4">
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-[var(--dash-primary)] animate-pulse" />
                    <span className="text-xs font-bold tracking-[0.2em] text-[var(--dash-primary)] uppercase">
                      {displayTypeLabel}
                    </span>
                    {activePromo ? (
                      <span
                        className={`promo-badge-flash relative overflow-visible rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] shadow-sm ${promoTone.badge}`}
                        style={{ '--promo-flash-color': getPromoFlashColor(activePromo?.colorTone) }}
                      >
                        {activePromo.label || activePromo.title || t('common.promo', 'Promo')}
                      </span>
                    ) : null}
                  </div>
                  <h1 className="text-4xl font-bold tracking-tight bg-gradient-to-r from-white via-white to-white/60 bg-clip-text text-transparent leading-tight md:text-5xl">
                    {displayName}
                  </h1>
                </div>
              </div>
              {brandImageSrc ? (
                /* eslint-disable-next-line @next/next/no-img-element */
                <img
                  src={brandImageSrc}
                  alt={`${brand} logo`}
                  aria-hidden="true"
                  className="pointer-events-none absolute right-0 top-0 h-16 w-16 select-none object-contain opacity-20 drop-shadow-[0_0_24px_rgba(255,255,255,0.06)] md:h-20 md:w-20"
                />
              ) : null}
              <div>
                <span
                  className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.25em] ${
                    (product.stock ?? 0) > 0
                      ? 'border-[var(--dash-success)] bg-[var(--dash-success-bg)] text-[var(--dash-success)]'
                      : 'border-[var(--dash-danger)] bg-[var(--dash-danger-bg)] text-[var(--dash-danger)]'
                  }`}
                >
                  {(product.stock ?? 0) > 0 ? (
                    <>
                      <Info className="h-3 w-3" />
                      {t('productDetails.inStock', 'In stock')}
                    </>
                  ) : (
                    <>
                      <XCircle className="h-3 w-3" />
                      {t('productDetails.outOfStock', 'Out of stock')}
                    </>
                  )}
                </span>
              </div>
              {displayDescription ? (
                <p className="text-base md:text-lg text-white/70 leading-relaxed max-w-xl">
                  {displayDescription}
                </p>
              ) : null}

              {/*
              <div>
                <p className="text-[11px] text-[var(--dash-primary)] uppercase font-extrabold tracking-[0.25em]">
                  {t('productDetails.price', 'Price')}
                </p>
                <p className="text-3xl md:text-4xl font-extrabold text-white">
                  {formatTND(product.price)}
                </p>
                <p className="text-xs text-white/60 mt-2">
                  {t('productDetails.priceHint', 'Includes standard configuration')}
                </p>
              </div>
              */}
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-bold tracking-widest text-white/50 uppercase">
                  {t('productDetails.keyDetails', 'Key details')}
                </h3>
                <span className="text-xs text-white/40">
                  {t('productDetails.specsIncluded', 'Specs included')}
                </span>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {hasHighlightSpec ? (
                  <div
                    className={`group rounded-2xl border border-white/10 bg-white/[0.03] p-4 transition-all duration-300 hover:bg-white/[0.05] hover:border-[var(--dash-main)] ${
                      shouldSpanPrimaryCard ? 'sm:col-span-2' : ''
                    }`}
                  >
                    <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-[var(--dash-primary)] opacity-60 transition-opacity md:group-hover:opacity-100">
                      <Info className="h-4 w-4" />
                    </div>
                    <p className="text-[10px] text-white/50 uppercase font-bold tracking-[0.18em]">
                      {highlightSpecLabel}
                    </p>
                    <p className="text-lg font-semibold text-white">{highlightSpecValue}</p>
                  </div>
                ) : null}
                {hasCapacity ? (
                  <div className="group rounded-2xl border border-white/10 bg-white/[0.03] p-4 transition-all duration-300 hover:bg-white/[0.05] hover:border-[var(--dash-main)]">
                    <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-[var(--dash-primary)] opacity-60 transition-opacity md:group-hover:opacity-100">
                      <Weight className="h-4 w-4" />
                    </div>
                    <p className="text-[10px] text-white/50 uppercase font-bold tracking-[0.18em]">
                      {t('productDetails.maxCapacity', 'Max Capacity')}
                    </p>
                    <p className="text-lg font-semibold text-white">{capacityValue}</p>
                  </div>
                ) : null}
                {hasPower ? (
                  <div className="group rounded-2xl border border-white/10 bg-white/[0.03] p-4 transition-all duration-300 hover:bg-white/[0.05] hover:border-[var(--dash-main)]">
                    <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-[var(--dash-primary)] opacity-60 transition-opacity md:group-hover:opacity-100">
                      <Zap className="h-4 w-4" />
                    </div>
                    <p className="text-[10px] text-white/50 uppercase font-bold tracking-[0.18em]">
                      {t('productDetails.powerSource', 'Power Source')}
                    </p>
                    <p className="text-lg font-semibold text-white">{powerValue}</p>
                  </div>
                ) : null}
                {extraDetailItems.map((item) => {
                  const Icon = detailIconByGroup[item.group] || Info
                  return (
                    <div
                      key={`extra-${item.key}`}
                      className="group rounded-2xl border border-white/10 bg-white/[0.03] p-4 transition-all duration-300 hover:bg-white/[0.05] hover:border-[var(--dash-main)]"
                    >
                      <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-[var(--dash-primary)] opacity-60 transition-opacity md:group-hover:opacity-100">
                        <Icon className="h-4 w-4" />
                      </div>
                      <p className="text-[10px] text-white/50 uppercase font-bold tracking-[0.18em]">
                        {item.label}
                      </p>
                      <p className="text-lg font-semibold text-white">{item.value}</p>
                    </div>
                  )
                })}
              </div>
            </div>

            {productShowrooms.length > 0 ? (
              <div className="space-y-4">
                <h3 className="text-xs font-bold tracking-widest text-white/50 uppercase">
                  {t('productDetails.showroomAvailability', 'Availability')}
                </h3>
                <div className="flex flex-wrap gap-3">
                  {productShowrooms.map((showroom, index) => {
                    const showroomName = getLocalizedShowroomName(showroom, language)
                    return (
                      <div
                        key={`showroom-${index}`}
                        className={`flex items-center gap-3 rounded-xl border px-4 py-3 transition-all duration-300 ${
                          showroom?.available
                            ? 'border-white/10 bg-white/[0.03] hover:border-white/20'
                            : 'border-white/5 bg-white/[0.02]'
                        }`}
                      >
                        <p
                          className={`max-w-[220px] text-sm font-medium leading-snug ${
                            showroom?.available ? 'text-white/85' : 'text-white/55'
                          }`}
                          style={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {showroomName || t('productDetails.showroomLabel', 'Showroom')}
                        </p>
                        <span
                          className={`inline-flex h-6 w-6 items-center justify-center rounded-full border ${
                            showroom?.available
                              ? 'border-[var(--dash-success)] bg-[var(--dash-success-bg)] text-[var(--dash-success)]'
                              : 'border-[var(--dash-danger)] bg-[var(--dash-danger-bg)] text-[var(--dash-danger)]'
                          }`}
                          aria-label={
                            showroom?.available
                              ? t('productDetails.available', 'Available')
                              : t('productDetails.unavailable', 'Unavailable')
                          }
                          title={
                            showroom?.available
                              ? t('productDetails.available', 'Available')
                              : t('productDetails.unavailable', 'Unavailable')
                          }
                        >
                          {showroom?.available ? (
                            <>
                              <CheckCircle2 className="h-4 w-4" />
                              <span className="sr-only">
                                {t('productDetails.available', 'Available')}
                              </span>
                            </>
                          ) : (
                            <>
                              <XCircle className="h-4 w-4" />
                              <span className="sr-only">
                                {t('productDetails.unavailable', 'Unavailable')}
                              </span>
                            </>
                          )}
                        </span>
                      </div>
                    )
                  })}
                </div>
                <div className="space-y-4 pt-4 border-t border-white/5">
                  <h3 className="text-xs font-bold tracking-widest text-white/50 uppercase">
                    {t('productDetails.safetyWarrantyTitle', 'Safety & Warranty')}
                  </h3>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="flex gap-4 items-start">
                      <div className="p-3 rounded-xl border border-white/10 bg-white/[0.03] text-[var(--dash-primary)]">
                        <ShieldCheck className="h-5 w-5" />
                      </div>
                      <div>
                        <h4 className="text-[11px] text-white/70 uppercase font-semibold tracking-[0.18em]">
                          {t('productDetails.safetyTitle', 'Safety')}
                        </h4>
                        <p className="mt-1 text-xs text-white/60 leading-relaxed">
                          {t(
                            'productDetails.safetyDescription',
                            'Inspected and ready for safe operation.',
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-4 items-start">
                      <div className="p-3 rounded-xl border border-white/10 bg-white/[0.03] text-[var(--dash-other)]">
                        <CheckCircle2 className="h-5 w-5" />
                      </div>
                      <div>
                        <h4 className="text-[11px] text-white/70 uppercase font-semibold tracking-[0.18em]">
                          {t('productDetails.warrantyTitle', 'Warranty')}
                        </h4>
                        <p className="mt-1 text-xs text-white/60 leading-relaxed">
                          {t(
                            'productDetails.warrantyDescription',
                            'Coverage details available on request.',
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            <button
              type="button"
              onClick={handleAddToCart}
              className="group relative flex h-16 w-full items-center justify-center gap-3 rounded-2xl bg-[var(--dash-primary)] font-bold tracking-tight text-white transition-all duration-300 hover:bg-[var(--dash-primary-hover)] hover:text-[var(--dash-black)] active:scale-[0.98]"
            >
              <span className="text-lg">{t('productDetails.addToCart', 'Add to Cart')}</span>
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </button>
          </div>
          </div>
        <section className="relative z-10 pt-16 pb-28">
          <div className="w-full">
          <div className="flex flex-col items-center text-center mb-6 gap-4">
            <div className="max-w-2xl">
              <h2 className="text-3xl font-bold text-white mb-2">
                {t('productDetails.technicalSpecifications')}
              </h2>
              <p className="text-white/60">
                {t(
                  'productDetails.specsSubtitle',
                  'Comprehensive mechanical and operational data for this product.',
                )}
              </p>
            </div>

            <div className="relative w-full max-w-full">
              <div className="flex items-center justify-center gap-2 overflow-x-auto md:overflow-visible snap-x snap-mandatory md:snap-none">
                <div className="flex gap-2 whitespace-nowrap pr-2 md:flex-wrap md:whitespace-normal md:justify-center">
                  {[
                    { key: 'all', label: t('common.all') },
                    { key: 'general', label: t('common.general') },
                    { key: 'performance', label: t('productDetails.specsGroupPerformance', 'Performance') },
                    { key: 'dimensions', label: t('productDetails.specsGroupDimensions', 'Dimensions') },
                    {
                      key: 'wheels_chassis',
                      label: t('productDetails.specsGroupWheelsChassis', 'Wheels & Chassis'),
                    },
                    { key: 'weight', label: t('productDetails.specsGroupWeight', 'Weight') },
                  ].map((tab) => {
                    const isActive = activeSpecGroup === tab.key
                    return (
                      <div key={tab.key} className="snap-start">
                        <button
                          onClick={() => setActiveSpecGroup(tab.key)}
                          className={`relative w-full px-4 py-2.5 rounded-xl text-xs md:text-sm font-semibold uppercase tracking-wide border transition-all ${
                            isActive
                              ? 'bg-[var(--dash-primary)] text-white border-[var(--dash-primary)] shadow-[0_10px_24px_rgba(var(--dash-main-rgb),0.25)]'
                              : 'text-white/80 border-white/10 hover:text-white hover:border-[var(--dash-main)]'
                          }`}
                        >
                          <span className="inline-flex items-center gap-2">
                            <span
                              className={`h-1.5 w-1.5 rounded-full ${
                                isActive ? 'bg-white' : 'bg-white/40'
                              }`}
                            />
                            {tab.label}
                          </span>
                        </button>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>

          <div className="overflow-hidden rounded-2xl border border-white/10 bg-black/30 shadow-[0_20px_60px_rgba(0,0,0,0.35)]">
            {filteredSpecs.length > 0 ? (
              <>
                <div className="grid gap-4 p-4 md:grid-cols-2 lg:grid-cols-3 lg:p-6">
                  {visibleSpecs.map(({ key, value, group }) => {
                    const SpecIcon = detailIconByGroup[group] || Info
                    const specLabel = fieldMetaByKey.get(key)?.label || formatSpecLabel(key)
                    const specUnit = fieldMetaByKey.get(key)?.unit ? ` ${fieldMetaByKey.get(key).unit}` : ''

                    return (
                      <div
                        key={key}
                        className="group rounded-2xl border border-white/[0.05] bg-white/[0.03] p-4 transition-all duration-300 hover:border-[var(--dash-main)] hover:bg-white/[0.05]"
                      >
                        <div className="mb-4 flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <span className="inline-flex rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-white/55">
                              {titleCase(group)}
                            </span>
                            <h4 className="mt-3 text-sm font-semibold leading-snug text-white/90">
                              {specLabel}
                            </h4>
                          </div>
                          <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-black/30 text-[var(--dash-primary)]">
                            <SpecIcon className="h-4 w-4" />
                          </span>
                        </div>
                        <p className="break-words text-base font-semibold text-white">
                          {formatSpecValue(value, t)}
                          {specUnit}
                        </p>
                      </div>
                    )
                  })}
                </div>

                {hiddenSpecs.length > 0 ? (
                  <>
                    <div
                      className={`grid transition-[grid-template-rows,opacity] duration-500 ease-in-out ${
                        showAllSpecs ? 'opacity-100' : 'opacity-0'
                      }`}
                      style={{ gridTemplateRows: showAllSpecs ? '1fr' : '0fr' }}
                    >
                      <div className="overflow-hidden">
                        <div className="grid gap-4 px-4 pb-4 md:grid-cols-2 lg:grid-cols-3 lg:px-6 lg:pb-6">
                          {hiddenSpecs.map(({ key, value, group }) => {
                            const SpecIcon = detailIconByGroup[group] || Info
                            const specLabel = fieldMetaByKey.get(key)?.label || formatSpecLabel(key)
                            const specUnit = fieldMetaByKey.get(key)?.unit ? ` ${fieldMetaByKey.get(key).unit}` : ''

                            return (
                              <div
                                key={key}
                                className="group rounded-2xl border border-white/[0.05] bg-white/[0.03] p-4 transition-all duration-300 hover:border-[var(--dash-main)] hover:bg-white/[0.05]"
                              >
                                <div className="mb-4 flex items-start justify-between gap-3">
                                  <div className="min-w-0">
                                    <span className="inline-flex rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-white/55">
                                      {titleCase(group)}
                                    </span>
                                    <h4 className="mt-3 text-sm font-semibold leading-snug text-white/90">
                                      {specLabel}
                                    </h4>
                                  </div>
                                  <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-black/30 text-[var(--dash-primary)]">
                                    <SpecIcon className="h-4 w-4" />
                                  </span>
                                </div>
                                <p className="break-words text-base font-semibold text-white">
                                  {formatSpecValue(value, t)}
                                  {specUnit}
                                </p>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 flex justify-center border-t border-white/10 px-4 pb-4 pt-4">
                      <button
                        type="button"
                        onClick={() => setShowAllSpecs((prev) => !prev)}
                        className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-white/70 transition-colors hover:border-[var(--dash-primary)]/40 hover:text-white"
                      >
                        {showAllSpecs
                          ? t('common.showLess', 'Show less')
                          : t('common.showMore', 'Show more')}
                      </button>
                    </div>
                  </>
                ) : null}
              </>
            ) : (
              <div className="p-12 text-center">
                <Info className="mx-auto mb-4 text-white/30" size={48} />
                <p className="font-medium text-white/60">{t('productDetails.noTechnicalSpecifications')}</p>
              </div>
            )}

            <div className="border-t border-white/10 bg-black/40 p-6">
              <div className="flex items-start gap-3">
                <ShieldCheck className="mt-0.5 w-5 h-5 text-green-400" />
                <p className="text-xs leading-relaxed text-white/60">
                  {t(
                    'productDetails.specsDisclaimer',
                    'All measurements provided are standard factory specifications. Performance values are based on nominal operating conditions. Optional attachments may alter rated capacity and dimensions.',
                  )}
                </p>
              </div>
            </div>
          </div>
          </div>
        </section>
      </div>
      </section>

      <section className="relative overflow-hidden border-t border-white/5 bg-[var(--dash-main)] py-24 text-white">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 opacity-80 bg-[url('/bg/2.webp')] bg-no-repeat bg-[size:100%_100%]" />
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_15%_20%,rgba(255,255,255,0.14)_0%,transparent_34%),radial-gradient(circle_at_85%_10%,rgba(255,255,255,0.08)_0%,transparent_30%),radial-gradient(circle_at_50%_85%,rgba(255,255,255,0.06)_0%,transparent_36%)]" />
        </div>
        <div className="container mx-auto">
          <ProductRelatedProductsSection
            items={relatedProducts}
          />
        </div>
      </section>

      <ShortcutsSection items={DEFAULT_SHORTCUTS} excludeCurrent={false} />

      {isMounted && lightboxOverlay ? createPortal(lightboxOverlay, document.body) : null}
    </div>
  )
}

export async function getServerSideProps(context) {
  const initialId = String(context?.params?.id || '').trim()
  const language = getLanguageFromCookie(context?.req?.headers?.cookie) || 'fr'
  const origin = getRequestOrigin(context?.req) || 'http://localhost:3000'

  try {
    const [productData, typeData, sparePartsData, productsData] = await Promise.all([
      fetchJsonOrThrow(`${origin}/api/products/${encodeURIComponent(initialId)}?lang=${language}`),
      fetchJsonOrThrow(`${origin}/api/product-types`).catch(() => []),
      fetchJsonOrThrow(`${origin}/api/spare-parts?lang=${language}&paginated=false`).catch(() => []),
      fetchJsonOrThrow(`${origin}/api/products?lang=${language}&paginated=false`).catch(() => []),
    ])

    if (!productData || typeof productData !== 'object' || productData.error) {
      return { notFound: true }
    }

    return {
      props: {
        initialId,
        initialProduct: productData,
        initialProductTypes: Array.isArray(typeData) ? typeData : [],
        initialProductsCatalog: Array.isArray(productsData) ? productsData : [],
      },
    }
  } catch (error) {
    if (error?.status === 404) {
      return { notFound: true }
    }

    console.error('Product SSR load failed', {
      id: initialId,
      language,
      error: error?.message || 'Unknown error',
    })

    return {
      props: {
        initialId,
      },
    }
  }
}
